import React, { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../../../features/utils/formatMoney";
import FilterCalendar from "../../form/FilterCalendar";
import useCustomers from "../../../hooks/useCustomers";
import ModalContainer from "../../common/ModalContainer";

function normalizeWallets(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];
  return [];
}

function getWalletCurrency(wallet) {
  return wallet?.currency || wallet?.currencyCode || "NGN";
}

function getActiveWallets(wallets) {
  return wallets.filter((wallet) => {
    const status = String(wallet?.status || wallet?.accountStatus || "Active");
    return (
      status.toLowerCase() !== "inactive" && status.toLowerCase() !== "disabled"
    );
  });
}

function CustomerStatsSection({ data, open, wallets = [], setWallets }) {
  const { getCustomerStatistics, getCustomerWallet } = useCustomers();
  const [loading, setLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletsModalOpen, setWalletsModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    range: "All",
    date: new Date().toLocaleDateString(),
  });
  const [stats, setStats] = useState({
    flight: {
      count: 0,
      amount: 0,
    },
    stay: {
      count: 0,
      amount: 0,
    },
    tour: {
      count: 0,
      amount: 0,
    },
    insurance: {
      count: 0,
      amount: 0,
    },
    total: {
      count: 0,
      amount: 0,
    },
  });

  const primaryWallet = wallets[0];
  const activeWallets = useMemo(() => getActiveWallets(wallets), [wallets]);
  const hasMultipleWallets = wallets.length > 1;

  const items = [
    {
      label: "Flight Sold",
      count: stats.flight.count,
      amount: stats.flight.amount,
    },
    {
      label: "Accommodations Sold",
      count: stats.stay.count,
      amount: stats.stay.amount,
    },
    {
      label: "Tours Sold",
      count: stats.tour.count,
      amount: stats.tour.amount,
    },
    {
      label: "Insurance Sold",
      count: stats.insurance.count,
      amount: stats.insurance.amount,
    },
    {
      label: "Total orders",
      count: stats.total.count,
      amount: stats.total.amount,
    },
    {
      label: "Wallet Balance",
      amount: primaryWallet?.balance || 0,
      currency: getWalletCurrency(primaryWallet),
      wallet: true,
    },
  ];

  const loadData = async () => {
    const promises = [
      getCustomerStatistics({
        setLoading,
        id: data?._id,
        dateFilter,
        callback: (statData) => {
          setStats(statData);
        },
      }),
      getCustomerWallet({
        setLoading: setWalletLoading,
        id: data?._id,
        callback: (walletData) => {
          setWallets?.(normalizeWallets(walletData));
        },
      }),
    ];
    await Promise.all(promises);
  };

  useEffect(() => {
    loadData();
  }, [data, dateFilter, open]);

  return (
    <div className="flex w-full flex-col p-4 gap-4 ">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 ">
        {items.map((item, itemIndex) => {
          const isWalletCard = item?.wallet;
          const isClickable = isWalletCard && hasMultipleWallets;

          return (
            <div
              className={`flex flex-1 border-black/10 bg-[#F9F9F9] p-4 rounded-lg flex-col ${
                isClickable
                  ? "cursor-pointer hover:border-primary1 hover:bg-[#F0F6FF] transition-colors"
                  : ""
              }`}
              key={itemIndex}
              onClick={
                isClickable ? () => setWalletsModalOpen(true) : undefined
              }
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={
                isClickable
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setWalletsModalOpen(true);
                      }
                    }
                  : undefined
              }
            >
              <div className="w-full flex items-center justify-between">
                <small>{item.label}</small>
                {loading && item.label !== "Wallet Balance" ? (
                  <div className="primaryLoader"></div>
                ) : (
                  <>
                    {item?.count ? (
                      <div className="px-2 rounded-md border-black/10 bg-white border pb-1">
                        {" "}
                        <small>{item.count.toLocaleString()}</small>{" "}
                      </div>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </div>
              {isWalletCard ? (
                <span>
                  {" "}
                  {walletLoading ? (
                    <div className="primaryLoader"></div>
                  ) : (
                    formatMoney(
                      item.amount,
                      item.currency ? item?.currency !== "NGN" : "",
                    )
                  )}
                </span>
              ) : (
                <span>
                  {" "}
                  {loading ? (
                    <div className="primaryLoader"></div>
                  ) : (
                    formatMoney(item.amount)
                  )}
                </span>
              )}
              {isWalletCard && hasMultipleWallets && !walletLoading ? (
                <small className="mt-2 text-[#6D7C94]">
                  {activeWallets.length} active wallet
                  {activeWallets.length === 1 ? "" : "s"}
                </small>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="flex flex-1 items-center justify-end">
        <div>
          <FilterCalendar
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
          />
        </div>
      </div>

      <ModalContainer
        open={walletsModalOpen}
        setOpen={setWalletsModalOpen}
        title="Wallets"
        subTitle="All wallets and balances"
        childClassname="max-w-[520px]"
      >
        <div className="p-4 flex flex-col gap-2">
          {wallets.length === 0 ? (
            <p className="text-sm text-[#6D7C94] py-6 text-center">
              No wallets found.
            </p>
          ) : (
            wallets.map((wallet, index) => {
              const currency = getWalletCurrency(wallet);
              return (
                <div
                  key={wallet?._id || `${currency}-${index}`}
                  className="flex items-center justify-between gap-3 w-full rounded-lg border border-[#E0E0E0] bg-[#F9F9F9] px-4 py-3"
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-semibold text-sm text-[#1A1A1A]">
                      {currency}
                    </span>
                    {wallet?.name ? (
                      <span className="text-xs text-[#6D7C94]">
                        {wallet.name}
                      </span>
                    ) : null}
                  </div>
                  <span className="text-sm font-semibold text-primary1 shrink-0">
                    {formatMoney(wallet?.balance || 0, currency)}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </ModalContainer>
    </div>
  );
}

export default CustomerStatsSection;
